/**
 * TaskTableView (Story 28.2) — a clone of the alerts AlertTableView. Same
 * table chrome (rounded-[22px] bg-muted/50 shadow-xs, 0.5px borders, header
 * bg-background + 0.4-opacity divider, sortable headers, 3-dot DropdownMenu
 * with DotsThreeVertical size 28) per the design-system directive §7. Columns
 * swapped to the task read-model.
 *
 * 🔴 REACT-QUERY-FREE: the inline status change is a PROP (onStatusChange),
 * never a hook in the view — co-located tests render this bare (no QueryClient).
 */

import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@provenleap/ui";
import {
  DotsThreeVertical,
  SortAscending,
  SortDescending,
  PencilSimple,
  Trash,
  Check,
  CaretDown,
} from "@phosphor-icons/react";
import type { Task, TaskStatus, SortOption } from "./types";
import { ClientAvatar } from "../clients/ClientAvatar";
import { TagPill } from "../clients/TagPill";
import { TableHeaderIcon } from "../shared/TableHeaderIcon";
import {
  STATUS_OPTIONS,
  STATUS_STYLES,
  STATUS_ICONS,
  statusLabel,
  assigneeLabel,
  dueLabel,
  isOverdue,
} from "./taskDisplay";

export interface TaskTableViewProps {
  tasks: Task[];
  canManage: boolean;
  onTaskClick?: (task: Task) => void;
  onEditClick?: (task: Task) => void;
  onDeleteClick?: (task: Task) => void;
  onStatusChange?: (task: Task, status: TaskStatus) => void;
  sortBy?: SortOption;
  onSortChange?: (sort: SortOption) => void;
  triggerRefs?: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
  /** Column ids the user has hidden (from the route, persisted to localStorage). */
  hiddenColumns?: string[];
  /** Retained for call-site compatibility; the Columns toolbar owns visibility. */
  onHideColumn?: (id: string) => void;
}

/**
 * Inline status control: a small DropdownMenu rendered as a button (only when
 * canManage && onStatusChange, else a span). Shows statusLabel + STATUS_STYLES
 * color; choosing a value calls onStatusChange(task, value).
 */
function StatusControl({
  task,
  canManage,
  onStatusChange,
}: {
  task: Task;
  canManage: boolean;
  onStatusChange?: (task: Task, status: TaskStatus) => void;
}) {
  const label = statusLabel(task.status);
  const classes = STATUS_STYLES[task.status];
  const StatusIcon = STATUS_ICONS[task.status];
  if (!canManage || !onStatusChange) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${classes}`}
      >
        <StatusIcon size={12} weight="bold" />
        {label}
      </span>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={`touch-target-sm inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-sm ${classes}`}
          aria-label={`Change status for ${task.title}`}
        >
          <StatusIcon size={12} weight="bold" />
          {label}
          <CaretDown size={12} weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="rounded-[23px] p-2 shadow-md"
        style={{ border: "0.5px solid var(--hairline)" }}
      >
        {STATUS_OPTIONS.map((opt) => {
          const OptIcon = STATUS_ICONS[opt.value];
          return (
            <DropdownMenuItem
              key={opt.value}
              className="min-h-11 text-[13px] cursor-pointer rounded-lg"
              onClick={() => onStatusChange(task, opt.value)}
            >
              <span className="w-4 mr-1 inline-flex justify-center">
                {opt.value === task.status && <Check size={14} />}
              </span>
              <OptIcon size={14} className="mr-1.5" />
              {opt.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TaskTableView({
  tasks,
  canManage,
  onTaskClick,
  onEditClick,
  onDeleteClick,
  onStatusChange,
  sortBy,
  onSortChange,
  triggerRefs,
  hiddenColumns = [],
}: TaskTableViewProps) {
  const isHidden = (id: string) => hiddenColumns.includes(id);
  return (
    <div
      className="overflow-hidden rounded-[22px] bg-muted/50 text-foreground shadow-xs"
      style={{ border: "0.5px solid var(--hairline)" }}
    >
      <table className="app-page-table w-full text-sm" aria-label="Task list">
        <thead className="relative bg-background">
          <tr>
            <th
              scope="col"
              className="text-left py-2 px-4 text-[13px] font-semibold text-foreground"
            >
              {onSortChange ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                  onClick={() =>
                    onSortChange(
                      sortBy === "title_asc" ? "title_desc" : "title_asc",
                    )
                  }
                >
                  <TableHeaderIcon name="task" />
                  Task
                  {(sortBy === "title_asc" || sortBy === "title_desc") &&
                    (sortBy === "title_asc" ? (
                      <SortAscending size={18} />
                    ) : (
                      <SortDescending size={18} />
                    ))}
                </button>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <TableHeaderIcon name="task" />
                  Task
                </span>
              )}
            </th>
            {!isHidden("status") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="status" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(
                          sortBy === "status_asc" ? "status_desc" : "status_asc",
                        )
                      }
                    >
                      Status
                      {(sortBy === "status_asc" || sortBy === "status_desc") &&
                        (sortBy === "status_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Status"
                  )}
                </span>
              </th>
            )}
            {!isHidden("tags") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden lg:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="tags" />
                  Tags
                </span>
              </th>
            )}
            {!isHidden("client") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden md:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="client" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(
                          sortBy === "client_asc" ? "client_desc" : "client_asc",
                        )
                      }
                    >
                      Client
                      {(sortBy === "client_asc" || sortBy === "client_desc") &&
                        (sortBy === "client_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Client"
                  )}
                </span>
              </th>
            )}
            {!isHidden("assignee") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden sm:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="assignee" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(
                          sortBy === "assignee_asc"
                            ? "assignee_desc"
                            : "assignee_asc",
                        )
                      }
                    >
                      Assignee
                      {(sortBy === "assignee_asc" ||
                        sortBy === "assignee_desc") &&
                        (sortBy === "assignee_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Assignee"
                  )}
                </span>
              </th>
            )}
            {!isHidden("dueDate") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="dueDate" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(
                          sortBy === "due_asc" ? "due_desc" : "due_asc",
                        )
                      }
                    >
                      Due date
                      {(sortBy === "due_asc" || sortBy === "due_desc") &&
                        (sortBy === "due_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Due date"
                  )}
                </span>
              </th>
            )}
            {canManage && (
              <th scope="col" className="py-2 px-4 w-16">
                <span className="sr-only">Actions</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => {
            const overdue = isOverdue(task.dueDate, task.status);
            return (
              <tr
                key={task.id}
                className={`bg-background hover:bg-brand/[0.02] transition-colors${onTaskClick ? " cursor-pointer" : ""}`}
                onClick={() => onTaskClick?.(task)}
                onKeyDown={(e) => {
                  if (onTaskClick && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    onTaskClick(task);
                  }
                }}
                tabIndex={onTaskClick ? 0 : undefined}
              >
                <td className="py-3 px-4">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{task.title}</p>
                      {task.description && (
                        <p className="text-[12px] text-foreground truncate">
                          {task.description}
                        </p>
                      )}
                    </div>
                  </div>
                </td>

                {!isHidden("status") && (
                  <td
                    className="py-3 px-4"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <StatusControl
                      task={task}
                      canManage={canManage}
                      onStatusChange={onStatusChange}
                    />
                  </td>
                )}

                {!isHidden("tags") && (
                  <td className="py-3 px-4 hidden lg:table-cell">
                    {task.tags.length > 0 ? (
                      <div className="flex flex-wrap gap-1 max-w-[220px]">
                        {task.tags.map((tag) => (
                          <TagPill key={tag.id} tag={tag} />
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                )}

                {!isHidden("client") && (
                  <td className="py-3 px-4 text-foreground hidden md:table-cell">
                    {task.clientName ? (
                      <div className="flex items-center gap-3">
                        <ClientAvatar
                          name={task.clientName}
                          logoUrl={task.clientLogoUrl}
                          size="sm"
                        />
                        <div className="min-w-0">
                          <p className="truncate">{task.clientName}</p>
                        </div>
                      </div>
                    ) : (
                      "General"
                    )}
                  </td>
                )}

                {!isHidden("assignee") && (
                  <td className="py-3 px-4 hidden sm:table-cell whitespace-nowrap text-foreground">
                    <span className="block truncate max-w-[200px]">
                      {assigneeLabel(task.assigneeName)}
                    </span>
                  </td>
                )}

                {!isHidden("dueDate") && (
                  <td
                    className={`py-3 px-4 whitespace-nowrap ${overdue ? "text-destructive font-medium" : "text-foreground"}`}
                  >
                    {dueLabel(task.dueDate)}
                  </td>
                )}

                {canManage && (
                  <td
                    className="py-3 px-4 text-right"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          ref={(el) => {
                            if (triggerRefs) triggerRefs.current[task.id] = el;
                          }}
                          variant="ghost"
                          size="icon"
                          className="min-h-11 min-w-11 cursor-pointer rounded-lg border border-transparent hover:bg-background hover:border-input transition-all"
                          aria-label={`Actions for ${task.title}`}
                        >
                          <DotsThreeVertical size={28} weight="bold" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="rounded-[23px] p-2 shadow-md"
                        style={{ border: "0.5px solid var(--hairline)" }}
                      >
                        <DropdownMenuItem
                          className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                          onClick={() => onEditClick?.(task)}
                        >
                          <PencilSimple size={14} className="mr-1" />
                          Edit Task
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="min-h-11 text-[13px] text-destructive cursor-pointer rounded-lg hover:!bg-destructive/10 focus:!bg-destructive/10 focus:!text-destructive focus:!border-destructive/30"
                          onClick={() => onDeleteClick?.(task)}
                        >
                          <Trash size={14} className="mr-1" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
